import { Form2Payload } from "../../../../../../../../scheduling/appointment/encounter/encounter-constant";
import { ExamCheckboxSection } from "../InitialPsychotherapy/InitialPsychotherapyConstant";

// Every key below is a real field on the visit note payload, so they are typed
// against it rather than as plain strings — a renamed or dropped key then fails
// the build instead of silently reading undefined off the note.
type NoteKey = keyof Form2Payload;

// ───────────────────────── Mental Status Examination ─────────────────────────
// Unlike the MSE the Psychotherapy / DAP / BIRP notes use — a checkbox card that
// persists a comma-separated string per section — the Progress Note MSE is one
// single-select per component, so each field holds a single option name.
//
// The option lists below are the Mental Status master list. They live in one
// place so the "configure Mental Status values from Settings" follow-up can swap
// this constant for the settings API without touching the modal or the report.

// Shared "not assessed" option name, appended to every field's option list and
// written by the "All Not Assessed" button.
export const MSE_NOT_ASSESSED = "notAssessed";

export type MseOption = { value: string; label: string };

export type MseField = {
  // Formik key. Prefixed with `mse` so it never collides with the
  // comma-separated MSE keys the other note types write (appearance, speech,
  // mood, thoughtProcess, insight …).
  key: NoteKey;
  label: string;
  options: MseOption[];
  // Value the "All Normal" button writes for this field.
  normalValue: string;
};

const NOT_ASSESSED_OPTION: MseOption = {
  value: MSE_NOT_ASSESSED,
  label: "Not Assessed",
};

// Every field ends with the shared "Not Assessed" option, so the master list
// below only spells out the clinical values.
const mseField = (
  key: NoteKey,
  label: string,
  normalValue: string,
  options: MseOption[]
): MseField => ({
  key,
  label,
  normalValue,
  options: [...options, NOT_ASSESSED_OPTION],
});

export const PROGRESS_MSE_FIELDS: MseField[] = [
  mseField("mseOrientation", "Orientation", "orientedX4", [
    { value: "orientedX4", label: "Oriented ×4" },
    { value: "orientedX3", label: "Oriented ×3" },
    { value: "orientedX2", label: "Oriented ×2" },
    { value: "orientedX1", label: "Oriented ×1" },
    { value: "disoriented", label: "Disoriented" },
  ]),
  mseField("mseGeneralAppearance", "General Appearance", "wellGroomed", [
    { value: "wellGroomed", label: "Well groomed" },
    { value: "neat", label: "Neat" },
    { value: "casual", label: "Casual" },
    { value: "disheveled", label: "Disheveled" },
    { value: "unkempt", label: "Unkempt" },
    { value: "bizarre", label: "Bizarre" },
  ]),
  mseField("mseDress", "Dress", "appropriate", [
    { value: "appropriate", label: "Appropriate" },
    { value: "casual", label: "Casual" },
    { value: "inappropriateForWeather", label: "Inappropriate for weather" },
    { value: "provocative", label: "Provocative" },
    { value: "careless", label: "Careless" },
  ]),
  mseField("mseMotorActivity", "Motor Activity", "normal", [
    { value: "normal", label: "Normal" },
    { value: "restless", label: "Restless" },
    { value: "agitated", label: "Agitated" },
    { value: "psychomotorRetardation", label: "Psychomotor retardation" },
    { value: "tremor", label: "Tremor" },
    { value: "tics", label: "Tics" },
  ]),
  mseField("mseInterviewBehavior", "Interview Behavior", "cooperative", [
    { value: "cooperative", label: "Cooperative" },
    { value: "guarded", label: "Guarded" },
    { value: "evasive", label: "Evasive" },
    { value: "hostile", label: "Hostile" },
    { value: "withdrawn", label: "Withdrawn" },
    { value: "overlyFamiliar", label: "Overly familiar" },
  ]),
  mseField("mseSpeech", "Speech", "normalRateAndVolume", [
    { value: "normalRateAndVolume", label: "Normal rate and volume" },
    { value: "pressured", label: "Pressured" },
    { value: "slow", label: "Slow" },
    { value: "loud", label: "Loud" },
    { value: "soft", label: "Soft" },
    { value: "slurred", label: "Slurred" },
    { value: "mute", label: "Mute" },
  ]),
  mseField("mseMood", "Mood", "euthymic", [
    { value: "euthymic", label: "Euthymic" },
    { value: "depressed", label: "Depressed" },
    { value: "anxious", label: "Anxious" },
    { value: "irritable", label: "Irritable" },
    { value: "angry", label: "Angry" },
    { value: "elevated", label: "Elevated" },
    { value: "labile", label: "Labile" },
  ]),
  mseField("mseAffect", "Affect", "fullRangeCongruent", [
    { value: "fullRangeCongruent", label: "Full range, congruent" },
    { value: "constricted", label: "Constricted" },
    { value: "blunted", label: "Blunted" },
    { value: "flat", label: "Flat" },
    { value: "labile", label: "Labile" },
    { value: "incongruent", label: "Incongruent with mood" },
  ]),
  mseField("mseInsight", "Insight", "good", [
    { value: "good", label: "Good" },
    { value: "fair", label: "Fair" },
    { value: "limited", label: "Limited" },
    { value: "poor", label: "Poor" },
    { value: "absent", label: "Absent" },
  ]),
  mseField(
    "mseJudgmentImpulseControl",
    "Judgment / Impulse Control",
    "intact",
    [
      { value: "intact", label: "Intact" },
      { value: "fair", label: "Fair" },
      { value: "mildlyImpaired", label: "Mildly impaired" },
      { value: "moderatelyImpaired", label: "Moderately impaired" },
      { value: "severelyImpaired", label: "Severely impaired" },
    ]
  ),
  mseField("mseMemory", "Memory", "intact", [
    { value: "intact", label: "Intact" },
    { value: "recentImpairment", label: "Recent memory impaired" },
    { value: "remoteImpairment", label: "Remote memory impaired" },
    { value: "moderatelyImpaired", label: "Moderately impaired" },
    { value: "severelyImpaired", label: "Severely impaired" },
  ]),
  mseField("mseAttentionConcentration", "Attention / Concentration", "intact", [
    { value: "intact", label: "Intact" },
    { value: "mildlyImpaired", label: "Mildly impaired" },
    { value: "moderatelyImpaired", label: "Moderately impaired" },
    { value: "severelyImpaired", label: "Severely impaired" },
    { value: "distractible", label: "Distractible" },
  ]),
  mseField(
    "mseThoughtProcess",
    "Thought Process",
    "linearLogicalGoalDirected",
    [
      {
        value: "linearLogicalGoalDirected",
        label: "Linear, logical, goal-directed",
      },
      { value: "circumstantial", label: "Circumstantial" },
      { value: "tangential", label: "Tangential" },
      { value: "flightOfIdeas", label: "Flight of ideas" },
      { value: "looseAssociations", label: "Loose associations" },
      { value: "thoughtBlocking", label: "Thought blocking" },
      { value: "perseverative", label: "Perseverative" },
    ]
  ),
  mseField("mseThoughtContent", "Thought Content", "noAbnormalContent", [
    { value: "noAbnormalContent", label: "No abnormal content" },
    { value: "suicidalIdeation", label: "Suicidal ideation" },
    { value: "homicidalIdeation", label: "Homicidal ideation" },
    { value: "delusions", label: "Delusions" },
    { value: "obsessions", label: "Obsessions" },
    { value: "paranoia", label: "Paranoia" },
    { value: "phobias", label: "Phobias" },
  ]),
  mseField("msePerception", "Perception", "noHallucinations", [
    { value: "noHallucinations", label: "No hallucinations" },
    { value: "auditoryHallucinations", label: "Auditory hallucinations" },
    { value: "visualHallucinations", label: "Visual hallucinations" },
    { value: "tactileHallucinations", label: "Tactile hallucinations" },
    { value: "illusions", label: "Illusions" },
    { value: "depersonalization", label: "Depersonalization" },
  ]),
  mseField("mseFunctionalStatus", "Functional Status", "noImpairment", [
    { value: "noImpairment", label: "No impairment" },
    { value: "mildImpairment", label: "Mild impairment" },
    { value: "moderateImpairment", label: "Moderate impairment" },
    { value: "severeImpairment", label: "Severe impairment" },
  ]),
];

// Free text printed under the MSE grid.
export const MSE_OVERALL_NOTE_KEY: NoteKey = "mseOverallNote";

export const PROGRESS_MSE_FIELD_KEYS: NoteKey[] = [
  ...PROGRESS_MSE_FIELDS.map((field) => field.key),
  MSE_OVERALL_NOTE_KEY,
];

// ─────────────────────────── Risk Assessment ───────────────────────────
// The Progress Note risk block is a structured set of fields rather than the
// single risk level + note the Psychotherapy Note modal captures, so it uses its
// own keys instead of `riskLevel` / `riskLevelNote`.

export const PROGRESS_RISK_LEVEL_OPTIONS: MseOption[] = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "imminent", label: "Imminent" },
];

// Need to Act / Plan to Act / Means to Act all share the same three answers.
export const PROGRESS_RISK_ACT_OPTIONS: MseOption[] = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
  { value: "na", label: "N/A" },
];

export const PROGRESS_RISK_ACT_FIELDS: { key: NoteKey; label: string }[] = [
  { key: "riskNeedToAct", label: "Need to Act" },
  { key: "riskPlanToAct", label: "Plan to Act" },
  { key: "riskMeansToAct", label: "Means to Act" },
];

export const PROGRESS_RISK_ASSESSMENT_FIELDS: NoteKey[] = [
  "riskDeniesAllAreas",
  "riskLevelOfRisk",
  ...PROGRESS_RISK_ACT_FIELDS.map((field) => field.key),
  "riskFactors",
  "riskProtectiveFactors",
  "riskAdditionalDetails",
];

// ─────────────────────────── Interventions Used ───────────────────────────
// Checkbox-card storage shape shared with the DAP / BIRP notes: the selection is
// one comma-separated string of option names plus a `${key}Note` free text.

// Option name that reveals the "Other Interventions" free text.
export const OTHER_INTERVENTIONS = "otherInterventions";

// Key the "Other Interventions" free text is persisted under.
export const OTHER_INTERVENTIONS_NOTE_KEY: NoteKey = "otherInterventionsNote";

export const PROGRESS_INTERVENTIONS_SECTIONS: ExamCheckboxSection[] = [
  {
    key: "interventionsUsed",
    label: "Interventions Used",
    items: [
      { name: "cbt", label: "CBT" },
      { name: "dbt", label: "DBT" },
      { name: "mindfulness", label: "Mindfulness" },
      { name: "guidedImagery", label: "Guided Imagery" },
      { name: "psychoeducation", label: "Psychoeducation" },
      { name: "cognitiveReframing", label: "Cognitive Reframing" },
      { name: "supportiveReflection", label: "Supportive Reflection" },
      {
        name: "structuredProblemSolving",
        label: "Structured Problem Solving",
      },
      { name: "communicationSkills", label: "Communication Skills" },
      { name: "symptomManagement", label: "Symptom Management" },
      { name: "rolePlay", label: "Role Play" },
      { name: "relaxation", label: "Relaxation" },
      { name: "explorationOfEmotions", label: "Exploration of Emotions" },
      {
        name: "explorationOfCopingPatterns",
        label: "Exploration of Coping Patterns",
      },
      { name: OTHER_INTERVENTIONS, label: "Other Interventions" },
    ],
  },
];

export const PROGRESS_INTERVENTIONS_FIELDS: NoteKey[] = [
  ...PROGRESS_INTERVENTIONS_SECTIONS.flatMap(
    (section) =>
      [section.key, section.noteKey ?? `${section.key}Note`] as NoteKey[]
  ),
  OTHER_INTERVENTIONS_NOTE_KEY,
];

// ─────────────────────────── Recommendation ───────────────────────────
// Single select, so it holds one option name rather than a comma-separated
// string.

export const PROGRESS_RECOMMENDATION_OPTIONS: MseOption[] = [
  {
    value: "continueCurrentTherapeuticFocus",
    label: "Continue current therapeutic focus",
  },
  {
    value: "changeTreatmentGoalsOrObjectives",
    label: "Change treatment goals or objectives",
  },
  { value: "terminateTreatment", label: "Terminate treatment" },
];

// Looks a value up in one of the option lists above so the note summary and the
// report can print the label the provider picked rather than the stored name.
export const optionLabel = (
  options: readonly MseOption[],
  value?: string | null
) => options.find((option) => option.value === value)?.label;
